import React, { useMemo } from 'react'
import jstz from 'jstz'
import { Controller, Resolver, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Button } from 'primereact/button'

import { SelectButton, SelectButtonOption } from '../../../../../common/uikit/SelectButton'
import {
    DeliveryInterval,
    ReportDeliverySchedule,
    ReportDeliveryType,
    ReportGetReportsForAdminApiResponse,
    useCompanyCreateReportDeliveryScheduleMutation,
    useCompanyUpdateReportDeliveryScheduleMutation,
} from '../../../../../common/redux/api/web-api'
import useText from '../../../../../common/hooks/useText'
import { Input } from '../../../../../common/uikit/Input'
import { Dropdown } from '../../../../../common/uikit/Dropdown'
import { Dialog } from '../../../../../common/uikit/Dialog'
import { Switch } from '../../../../../common/uikit/Switch'
import { useToast } from '../../../../../common/uikit/ToastProvider'

const formId = 'report-delivery-form'

type ReportDeliveryFormValues = {
    reportId?: number
    deliveryInterval?: DeliveryInterval
    deliveryType: ReportDeliveryType
    receiverName?: string | null
    receiverEmail?: string | null
    mailSubject?: string | null
    ftpUrl?: string | null
    ftpUserName?: string | null
    ftpPassword?: string | null
    active?: boolean
}

type AutomaticReportDeliveryDialogProps = {
    companyId: string
    reports?: ReportGetReportsForAdminApiResponse
    visible: boolean
    onHide: () => void
    selectedReportDeliverySchedule?: ReportDeliverySchedule
}

export const AutomaticReportDeliveryDialog = ({
    companyId,
    reports,
    visible,
    onHide,
    selectedReportDeliverySchedule,
}: AutomaticReportDeliveryDialogProps) => {
    const text = useText()
    const toast = useToast()

    const reportDeliveryIntervalsList = [
        { value: 'Day', label: text('lblDayInterval') },
        { value: 'Month', label: text('lblMonthInterval') },
        { value: 'Year', label: text('lblYearInterval') },
        { value: 'Quarter', label: text('lblQuarterInterval') },
    ]

    const reportDeliverySchema = yup.object().shape({
        reportId: yup.number().nullable().required(text('errReportRequired')),
        deliveryInterval: yup
            .mixed<DeliveryInterval>()
            .oneOf(['Day', 'Month', 'Year', 'Quarter'])
            .required(text('errDeliveryIntervalRequired')),
        deliveryType: yup.mixed<ReportDeliveryType>().oneOf(['Email', 'Ftp']).required(text('errDeliveryTypeRequired')),

        receiverName: yup.string().when('deliveryType', {
            is: 'Email',
            then: yup.string().required(text('errReceiverNameRequired')),
            otherwise: yup.string().nullable().notRequired(),
        }),
        receiverEmail: yup
            .string()
            .nullable()
            .when('deliveryType', {
                is: 'Email',
                then: yup.string().email(text('lblInvalidEmail')).required(text('errReceiverEmailRequired')),
                otherwise: yup.string().nullable().notRequired(),
            }),
        mailSubject: yup.string().when('deliveryType', {
            is: 'Email',
            then: yup.string().required(text('errMailSubjectRequired')),
            otherwise: yup.string().nullable().notRequired(),
        }),

        ftpUrl: yup.string().when('deliveryType', {
            is: 'Ftp',
            then: yup.string().url(text('errFtpUrlInvalid')).required(text('errFtpUrlRequired')),
            otherwise: yup.string().nullable().notRequired(),
        }),
        ftpUserName: yup.string().when('deliveryType', {
            is: 'Ftp',
            then: yup.string().required(text('errFtpUserNameRequired')),
            otherwise: yup.string().nullable().notRequired(),
        }),
        ftpPassword: yup.string().when('deliveryType', {
            is: 'Ftp',
            then: yup.string().required(text('errFtpPasswordRequired')),
            otherwise: yup.string().nullable().notRequired(),
        }),

        active: yup.boolean().notRequired(),
    })

    const getFormValues = (schedule?: ReportDeliverySchedule) => ({
        reportId: schedule?.reportId,
        deliveryInterval: schedule?.deliveryInterval,
        deliveryType: schedule?.deliveryType ?? ('Email' as const),
        receiverName: schedule?.receiverName ?? '',
        receiverEmail: schedule?.receiverEmail ?? '',
        mailSubject: schedule?.mailSubject ?? '',
        ftpUrl: schedule?.ftpUrl ?? '',
        ftpUserName: schedule?.ftpUserName ?? '',
        ftpPassword: schedule?.ftpPassword ?? '',
        active: schedule?.active ?? false,
    })

    const {
        control,
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors, isSubmitted, isValid, isSubmitting },
    } = useForm<ReportDeliveryFormValues>({
        defaultValues: getFormValues(selectedReportDeliverySchedule),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        resolver: yupResolver(reportDeliverySchema) as unknown as Resolver<ReportDeliveryFormValues>,
    })

    const deliveryType = watch('deliveryType')

    const [updateReport, { isLoading: isUpdating }] = useCompanyUpdateReportDeliveryScheduleMutation()
    const [createReport, { isLoading: isCreating }] = useCompanyCreateReportDeliveryScheduleMutation()

    const handleOnHide = () => {
        reset(getFormValues(selectedReportDeliverySchedule))
        onHide()
    }

    const saveReportDelivery = async (formValues: ReportDeliveryFormValues) => {
        try {
            const payload: ReportDeliverySchedule = {
                ...selectedReportDeliverySchedule,
                ...formValues,
                ftpUrl: formValues.deliveryType === 'Ftp' ? formValues.ftpUrl : undefined,
                ftpUserName: formValues.deliveryType === 'Ftp' ? formValues.ftpUserName : undefined,
                ftpPassword: formValues.deliveryType === 'Ftp' ? formValues.ftpPassword : undefined,
                receiverName: formValues.deliveryType === 'Email' ? formValues.receiverName : undefined,
                receiverEmail: formValues.deliveryType === 'Email' ? formValues.receiverEmail : undefined,
                mailSubject: formValues.deliveryType === 'Email' ? formValues.mailSubject : undefined,
                timeZone: jstz.determine().name(),
            }

            if (selectedReportDeliverySchedule) {
                await updateReport({
                    companyId,
                    reportDeliverySchedule: payload,
                }).unwrap()
            } else {
                await createReport({
                    companyId,
                    reportDeliverySchedule: payload,
                }).unwrap()
            }

            toast.notifySuccess(text('msgReportSaved'))
            handleOnHide()
        } catch (_) {
            toast.notifyError(text('msgReportNotSaved'))
        }
    }

    const reportsOptions = useMemo(
        () =>
            reports?.map((nextReport) => ({
                value: nextReport.id,
                label: nextReport.name,
            })) || [],
        [reports]
    )

    const deliveryTypeOptions: SelectButtonOption<ReportDeliveryType>[] = [
        { value: 'Email', label: text('lblEmail') },
        { value: 'Ftp', label: text('lblFtpDeliveryType') },
    ]
    const saving = isCreating || isUpdating || isSubmitting
    const saveDisabled = saving || (isSubmitted && !isValid)
    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" text onClick={handleOnHide} />
            <Button
                label={text(selectedReportDeliverySchedule ? 'btnSave' : 'btnCreate')}
                type="submit"
                form={formId}
                disabled={saveDisabled}
                loading={saving}
            />
        </div>
    )

    return (
        <Dialog
            header={text(
                selectedReportDeliverySchedule
                    ? 'lblEditAutomaticReportDeliveryDialogTitle'
                    : 'lblNewAutomaticReportDeliveryDialogTitle'
            )}
            visible={visible}
            onHide={handleOnHide}
            footer={footer}
        >
            <form id={formId} className="flex flex-column gap-4" onSubmit={handleSubmit(saveReportDelivery)} noValidate>
                <Controller
                    name="reportId"
                    control={control}
                    render={({ field }) => (
                        <Dropdown
                            options={reportsOptions}
                            id="report-id-select"
                            data-testid="report-id-select"
                            label={text('lblReport')}
                            value={field.value}
                            onChange={(option) => field.onChange(option.value)}
                            error={errors.reportId?.message}
                            required
                        />
                    )}
                />
                <Controller
                    name="deliveryInterval"
                    control={control}
                    render={({ field }) => (
                        <Dropdown
                            options={reportDeliveryIntervalsList}
                            label={text('lblReportDeliveryIntervalField')}
                            id="interval-select"
                            data-testid="interval-select"
                            value={field.value}
                            onChange={(option) => field.onChange(option.value)}
                            error={errors.deliveryInterval?.message}
                            required
                        />
                    )}
                />
                <div>
                    <Controller
                        name="deliveryType"
                        control={control}
                        render={({ field }) => (
                            <SelectButton<ReportDeliveryType>
                                id="report-delivery-type"
                                label={text('lblReportDeliveryTypeField')}
                                options={deliveryTypeOptions}
                                onChange={field.onChange}
                                value={field.value}
                            />
                        )}
                    />
                </div>

                <h3 className="m-0 text-xl text-color-secondary">{text('lblReportDeliverySettings')}</h3>
                {deliveryType === 'Email' ? (
                    <div className="flex flex-column gap-3" key="email">
                        <Input
                            id="receiverName"
                            label={text('lblReceiverNameField')}
                            maxLength={50}
                            error={errors.receiverName?.message}
                            required
                            {...register('receiverName')}
                        />
                        <Input
                            id="receiverEmail"
                            label={text('lblReceiverEmailField')}
                            type="email"
                            autoComplete="off"
                            maxLength={100}
                            error={errors.receiverEmail?.message}
                            required
                            {...register('receiverEmail')}
                        />
                        <Input
                            id="receiverSubject"
                            label={text('lblEmailSubjectField')}
                            autoComplete="off"
                            maxLength={50}
                            error={errors.mailSubject?.message}
                            required
                            {...register('mailSubject')}
                        />
                    </div>
                ) : (
                    <div className="flex flex-column gap-3" key="ftp">
                        <Input
                            id="ftpURL"
                            label={text('lblFtpUrlField')}
                            type="url"
                            error={errors.ftpUrl?.message}
                            required
                            {...register('ftpUrl')}
                        />
                        <Input
                            id="ftpUserName"
                            label={text('lblFtpUsernameField')}
                            autoComplete="new-password"
                            error={errors.ftpUserName?.message}
                            required
                            {...register('ftpUserName')}
                        />
                        <Input
                            id="ftpPassword"
                            label={text('lblFtpPasswordField')}
                            type="password"
                            autoComplete="new-password"
                            error={errors.ftpPassword?.message}
                            required
                            {...register('ftpPassword')}
                        />
                    </div>
                )}

                <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                        <Switch
                            inputId="report-delivery-active"
                            label={text('lblActive')}
                            checked={field.value ?? false}
                            onChange={(event) => field.onChange(event.value)}
                        />
                    )}
                />
            </form>
        </Dialog>
    )
}
